import { Component, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  Subject,
  catchError,
  filter,
  first,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  retry,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import {
  LandingConfig,
  LandingForm,
  LandingConfigResponse,
  LandingFormValue,
} from '../../../domain/landing-config.interface';
import { LandingService } from '../../../infrastructure/services/landing.service';
import { selectConfig } from '../../../infrastructure/store/selectors/config.selector';
import { FILL_SAVE_MESSAGE, FILL_SUBMIT_MESSAGE } from '../fill/fill.component';
import { normalizeSections } from './designs/shared/landing-utils';

// Clave de la config remota (Access Control) con el diseño de la landing ('landing_1'...'landing_5').
// Si no viene, se usa el template_id de /config y, sin ninguno, LandingDesign5
const LANDING_TEMPLATE_CONFIG_KEY = 'FORM_LANDING_TEMPLATE';

// Reintentos para obtener el pdf_key después de enviar el formulario (el PDF se genera en el backend)
const PDF_RETRY_COUNT = 5;
const PDF_RETRY_DELAY_MS = 2000;
// Formularios consultados a la vez al buscar el PDF de un ?formValueId al abrir la landing
const PDF_LOOKUP_CONCURRENCY = 3;

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss']
})
export class LandingComponent implements OnInit, OnDestroy {
  config: LandingConfig;
  // Elige el componente de diseño (ngSwitch en el template); sin coincidencia -> LandingDesign5
  templateId: string;
  loading = true;
  // Comprobando si el ?formValueId ya tiene un PDF: no se pintan las cards hasta saberlo
  checkingPdf = false;

  // Flujo: card -> confirmación -> vista del consentimiento (description) -> formulario.
  // Si el formulario de la card ya está diligenciado para este formValueId: card -> PDF
  confirmForm: LandingForm = null;
  viewForm: LandingForm = null;
  viewHtml: SafeHtml = null;
  // Formulario de aceptación/negación que reemplaza al consentimiento en el visor
  fillUrl: SafeResourceUrl = null;
  // PDF del formulario ya diligenciado (URL firmada de /pdfs/presigned)
  pdfUrl: SafeResourceUrl = null;
  // formId abierto en fillUrl; al enviarse se busca el pdf_key de su valor
  private fillFormId: string = null;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private landingService: LandingService,
    private toastAlert: ToastrService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private store: Store
  ) {}

  ngOnInit(): void {
    forkJoin({
      remoteConfig: this.store.select(selectConfig).pipe(
        filter((x) => x.data['FORM_API']),
        take(1)
      ),
      res: this.landingService.getConfig(),
    })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ remoteConfig, res }: { remoteConfig: { data: any }; res: LandingConfigResponse }) => {
          this.config = res?.config;
          this.templateId = remoteConfig.data[LANDING_TEMPLATE_CONFIG_KEY] || res?.template_id;
          if (this.config && this.formValueId) {
            this.openFilledPdf();
          } else {
            this.loading = false;
          }
        },
        error: (err) => {
          this.loading = false;
          this.toastAlert.error(err?.error?.error || 'Internal Error', 'Error');
        },
      });
  }

  // Color principal de la landing (el del header del /config); lo consumen los estilos vía $primary/$accent
  @HostBinding('style.--landing-primary-color')
  get primaryColor(): string {
    return this.config?.header?.background_color || null;
  }

  // customKey de los valores: viene en la URL de la landing (?formValueId=...)
  get formValueId(): string {
    return this.route.snapshot.queryParamMap.get('formValueId') || '';
  }

  // Enlace del correo con ?formValueId: si alguna card ya está diligenciada para ese formValueId se abre
  // directamente su PDF; si no, se muestran las cards. Mientras se comprueba no se pintan las cards.
  // Se consultan pocos formularios a la vez y se para en el primero con pdf_key. Aquí un fallo al
  // consultar un formulario no bloquea: se toma como no diligenciado
  private openFilledPdf(): void {
    const formIds = normalizeSections(this.config)
      .reduce((acc, section) => acc.concat(section.forms || []), [] as LandingForm[])
      .reduce(
        (acc, form) =>
          acc.concat(
            [form.acceptance_form_id, form.negatino_form_id].filter(Boolean).map((formId) => ({ form, formId }))
          ),
        [] as { form: LandingForm; formId: string }[]
      );
    this.checkingPdf = true;
    from(formIds)
      .pipe(
        mergeMap(
          ({ form, formId }) =>
            this.landingService.getFormValue(formId, this.formValueId).pipe(
              map((value: LandingFormValue) => ({ form, pdfKey: value?.pdf_key })),
              catchError(() => of(null))
            ),
          PDF_LOOKUP_CONCURRENCY
        ),
        first((result) => !!result?.pdfKey, null),
        switchMap((found: { form: LandingForm; pdfKey: string }) =>
          found
            ? this.landingService.getPdfUrl(found.pdfKey).pipe(map((res) => ({ form: found.form, url: res.url })))
            : of(null)
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (found: { form: LandingForm; url: string }) => {
          this.loading = false;
          this.checkingPdf = false;
          if (!found) return;
          this.viewForm = found.form;
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(found.url);
        },
        error: (err) => {
          this.loading = false;
          this.checkingPdf = false;
          this.toastAlert.error(err?.error?.error || 'No se pudo cargar el PDF', 'Error');
        },
      });
  }

  // Si ya hay un valor diligenciado (con PDF) para la card, se muestra el PDF; si no, sigue el flujo normal
  onSelectForm(form: LandingForm): void {
    if (!this.formValueId) {
      this.confirmForm = form;
      return;
    }
    this.loading = true;
    this.findPdfKey(form)
      .pipe(
        switchMap((pdfKey) =>
          pdfKey ? this.landingService.getPdfUrl(pdfKey).pipe(map((res) => res.url)) : of(null)
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (url: string) => {
          this.loading = false;
          if (url) {
            this.viewForm = form;
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          } else {
            this.confirmForm = form;
          }
        },
        error: (err) => {
          this.loading = false;
          this.toastAlert.error(err?.error?.error || 'No se pudo cargar el formulario', 'Error');
        },
      });
  }

  // pdf_key del valor del formulario de aceptación o de negación de la card (el que esté diligenciado).
  // Si falla la consulta de algún valor, el error llega al subscribe (toast) y no se abre nada
  private findPdfKey(form: LandingForm): Observable<string> {
    const formIds = [form.acceptance_form_id, form.negatino_form_id].filter(Boolean);
    if (!formIds.length) return of(null);
    return forkJoin(
      formIds.map((formId) => this.landingService.getFormValue(formId, this.formValueId))
    ).pipe(map((values: LandingFormValue[]) => values.find((value) => value?.pdf_key)?.pdf_key || null));
  }

  onCancelConfirm(): void {
    this.confirmForm = null;
  }

  onAcceptConfirm(): void {
    this.viewForm = this.confirmForm;
    this.confirmForm = null;
    // description es un documento HTML completo con sus propios estilos: se pinta en un iframe
    // sandbox (sin scripts) para que su CSS no afecte a la página
    this.viewHtml = this.sanitizer.bypassSecurityTrustHtml(this.viewForm.description || '');
  }

  onCloseView(): void {
    this.viewForm = null;
    this.viewHtml = null;
    this.fillUrl = null;
    this.fillFormId = null;
    this.pdfUrl = null;
  }

  // Ambos ids -> "¿Acepta el procedimiento?" Sí/No; uno solo -> un botón que abre ese; ninguno -> nada
  hasAcceptance(form: LandingForm): boolean {
    return !!form?.acceptance_form_id;
  }

  hasNegation(form: LandingForm): boolean {
    return !!form?.negatino_form_id;
  }

  // Carga /fill del mismo origen en el visor; customKey = formValueId de la URL de la landing
  openForm(formId: string): void {
    if (!formId) return;
    const params = new URLSearchParams({
      formId,
      versionId: 'latest',
      page: '1',
      customKey: this.formValueId,
    });
    const url = `${window.location.origin}/fill?${params.toString()}`;
    this.fillFormId = formId;
    this.fillUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // /fill (en el iframe) avisa con postMessage cuando el formulario se guarda o se envía correctamente
  @HostListener('window:message', ['$event'])
  onFillMessage(event: MessageEvent): void {
    if (event.origin !== window.location.origin || !this.fillUrl) return;
    const messages = {
      [FILL_SUBMIT_MESSAGE]: 'Formulario enviado correctamente',
      [FILL_SAVE_MESSAGE]: 'Formulario guardado correctamente',
    };
    const message = messages[event.data?.type];
    if (!message) return;
    this.toastAlert.success(message);
    if (event.data.type === FILL_SUBMIT_MESSAGE) {
      this.showSubmittedPdf(this.fillFormId, event.data.data?.pdf_key);
    } else {
      this.onCloseView();
    }
  }

  // Tras el envío, el visor pasa del formulario a su PDF. Si la respuesta del envío no trae pdf_key,
  // se consulta el valor; el PDF puede tardar en generarse, así que se reintenta unas veces
  private showSubmittedPdf(formId: string, pdfKey?: string): void {
    this.loading = true;
    const pdfKey$: Observable<string> = pdfKey
      ? of(pdfKey)
      : this.landingService.getFormValue(formId, this.formValueId).pipe(
          map((value: LandingFormValue) => {
            if (!value?.pdf_key) throw new Error('PDF no generado');
            return value.pdf_key;
          }),
          retry({ count: PDF_RETRY_COUNT, delay: PDF_RETRY_DELAY_MS })
        );
    pdfKey$
      .pipe(
        switchMap((key) => this.landingService.getPdfUrl(key)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.fillUrl = null;
          this.fillFormId = null;
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.url);
        },
        error: () => {
          this.loading = false;
          this.toastAlert.error('No se pudo cargar el PDF del formulario', 'Error');
          this.onCloseView();
        },
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
